// "Activá los avisos de tus pedidos" (decisión D53): en Mensajes (franja) y en Tu cuenta (Mensajes con la distribuidora
// y la fila de los avisos). Solo pide permiso al tocar; si el navegador no puede o el iPhone no tiene la tienda
// instalada, lo explica.
import { Icono } from '@reparto/ui';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { mensajeDeError } from '../datos/api';
import { cargarResumen, useChat } from '../datos/chat';
import { useCuenta, useNegocio } from '../datos/cuenta';
import { activarPush, desactivarPush, type EstadoPush, estadoPush } from '../datos/push';
import { useSesion } from '../datos/sesion';
import { Aviso, Boton } from './base';

/** Tu cuenta: los mensajes con la distribuidora (con los sin leer) y los avisos de los pedidos. */
export function SeccionMensajes() {
  const negocio = useNegocio();
  const empresa = useCuenta((s) => s.perfil?.marca?.nombre ?? s.perfil?.empresa.nombre ?? 'la distribuidora');
  const habilitado = useChat((s) => s.habilitado);
  const noLeidos = useChat((s) => (s.negocioId === negocio?.id ? s.noLeidos : 0));
  useEffect(() => {
    if (negocio) void cargarResumen(negocio.id, { siVencido: true });
  }, [negocio?.id]);
  if (!negocio || !habilitado) return null;
  return (
    <section
      aria-label="Mensajes y avisos"
      className="divide-y divide-stone-100 rounded-3xl border border-stone-200 bg-white"
    >
      <Link
        to="/mensajes"
        className="flex items-center gap-3 rounded-t-3xl p-4 hover:bg-stone-50 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-600"
      >
        <Icono nombre="chat" tamano={26} className="shrink-0 text-primary-700" />
        <span className="min-w-0 flex-1">
          <span className="block text-[17px] font-semibold text-stone-900">Mensajes</span>
          <span className="block text-[15px] text-stone-600">Con {empresa}, sobre tus pedidos</span>
        </span>
        {noLeidos ? (
          <span className="grid h-7 min-w-7 place-items-center rounded-full bg-primary-600 px-2 text-sm font-bold text-sobre-marca tabular-nums">
            {noLeidos > 99 ? '99+' : noLeidos}
          </span>
        ) : null}
        <Icono nombre="chevron_right" tamano={24} className="shrink-0 text-stone-500" />
      </Link>
      <FilaAvisos />
    </section>
  );
}

function useEstadoPush() {
  const clave = useChat((s) => s.clavePush);
  const [estado, setEstado] = useState<EstadoPush | null>(null);
  useEffect(() => {
    let vivo = true;
    void estadoPush(clave).then((e) => vivo && setEstado(e));
    return () => {
      vivo = false;
    };
  }, [clave]);
  return { clave, estado, setEstado };
}

/** Franja arriba de la conversación, mientras los avisos estén apagados y se puedan prender. */
export function FranjaAvisos() {
  const { clave, estado, setEstado } = useEstadoPush();
  const sesion = useSesion((s) => s.sesion?.dispositivo_id ?? '');
  const [activando, setActivando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cerrada, setCerrada] = useState(false);
  if (cerrada || (estado !== 'apagado' && estado !== 'iphone_instalar')) return null;
  if (estado === 'iphone_instalar')
    return (
      <Aviso tono="info" icono="notifications" titulo="Avisos de tus pedidos">
        En el iPhone, agregá la tienda a la pantalla de inicio (Compartir → Agregar a inicio) para recibir los avisos.
      </Aviso>
    );
  return (
    <Aviso
      tono="marca"
      icono="notifications_active"
      titulo="Activá los avisos de tus pedidos"
      accion={
        <>
          <Boton
            tamano="sm"
            className="h-11!"
            icono="notifications"
            cargando={activando}
            onClick={async () => {
              setActivando(true);
              setError(null);
              try {
                setEstado(await activarPush(clave, sesion));
              } catch (e) {
                setError(mensajeDeError(e));
              } finally {
                setActivando(false);
              }
            }}
          >
            Activar avisos
          </Boton>
          <Boton variante="secundario" tamano="sm" className="h-11!" onClick={() => setCerrada(true)}>
            Ahora no
          </Boton>
        </>
      }
    >
      Te avisamos cuando la distribuidora te responda o cambie tu pedido, aunque tengas la tienda cerrada.
      {error ? <span className="mt-1 block font-medium text-rose-800">{error}</span> : null}
    </Aviso>
  );
}

const TEXTO: Record<EstadoPush, string> = {
  activo: 'Activados en este celular',
  apagado: 'Apagados',
  bloqueado: 'Bloqueados en el navegador: permitilos en la configuración del sitio',
  no_soportado: 'Este navegador no los tiene',
  iphone_instalar: 'En el iPhone, primero agregá la tienda a la pantalla de inicio',
  sin_servidor: 'No disponibles por ahora',
};

/** Fila de Tu cuenta: el estado de los avisos y el botón para prenderlos o apagarlos. */
export function FilaAvisos() {
  const { clave, estado, setEstado } = useEstadoPush();
  const sesion = useSesion((s) => s.sesion?.dispositivo_id ?? '');
  const [ocupado, setOcupado] = useState(false);
  if (!estado) return null;
  const puede = estado === 'activo' || estado === 'apagado';
  return (
    <div className="flex items-center gap-3 p-4">
      <Icono
        nombre={estado === 'activo' ? 'notifications_active' : 'notifications_off'}
        tamano={26}
        className="shrink-0 text-primary-700"
      />
      <div className="min-w-0 flex-1">
        <p className="text-[17px] font-semibold text-stone-900">Avisos de tus pedidos</p>
        <p className="text-[15px] text-stone-600">{TEXTO[estado]}</p>
      </div>
      {puede ? (
        <Boton
          variante={estado === 'activo' ? 'secundario' : 'primario'}
          tamano="sm"
          className="h-11! shrink-0"
          cargando={ocupado}
          onClick={async () => {
            setOcupado(true);
            try {
              if (estado === 'activo') {
                await desactivarPush();
                setEstado('apagado');
              } else setEstado(await activarPush(clave, sesion));
            } finally {
              setOcupado(false);
            }
          }}
        >
          {estado === 'activo' ? 'Apagar' : 'Activar'}
        </Boton>
      ) : null}
    </div>
  );
}
